import React, { useState, useEffect } from 'react'
import { Box, Button, Group, TextInput, Select, LoadingOverlay, Text, Avatar, Loader } from '@mantine/core'
import { getAllCountryList, getAllProvinceList, createCity, updateCity } from '../../../services/location'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX } from '@tabler/icons-react'

const FormCity = ({ dataDetail, onCloseModal, reloadList }) => {
    const [loading, setLoading] = useState(false)
    const [loadingCountries, setLoadingCountries] = useState(false)
    const [loadingProvinces, setLoadingProvinces] = useState(false)
    const [countryList, setCountryList] = useState([])
    const [provinceList, setProvinceList] = useState([])

    // Form state
    const [form, setForm] = useState({
        name: '',
        country_id: null,
        province_id: null,
    })

    // Validation state
    const [errors, setErrors] = useState({
        name: '',
        country_id: '',
        province_id: '',
    })

    useEffect(() => {
        fetchCountries()
        if (dataDetail) {
            setForm({
                name: dataDetail.name || '',
                country_id: dataDetail.country_id ? String(dataDetail.country_id) : null,
                province_id: dataDetail.province_id ? String(dataDetail.province_id) : null,
            })
            // If editing, we need to fetch provinces for the selected country immediately
            if (dataDetail.country_id) {
                fetchProvinces(dataDetail.country_id)
            }
        }
    }, [dataDetail])

    const fetchCountries = async () => {
        setLoadingCountries(true)
        try {
            const response = await getAllCountryList({ 'page[limit]': 300 })
            if (response && response.data) {
                const mappedCountries = response.data.map(c => ({
                    value: String(c.attributes.id),
                    label: c.attributes.name,
                    iso_code_2: c.attributes.iso_code_2
                }))
                setCountryList(mappedCountries)
            }
        } catch (error) {
            console.error("Failed to fetch countries", error)
            notifications.show({
                title: 'Error',
                message: 'Failed to load country list',
                color: 'red',
            })
        } finally {
            setLoadingCountries(false)
        }
    }

    const fetchProvinces = async (countryId) => {
        if (!countryId) {
            setProvinceList([])
            return
        }
        setLoadingProvinces(true)
        try {
            // Assuming the API supports filtering by country_id
            const response = await getAllProvinceList({
                'filter[country_id]': countryId,
                'page[limit]': 300 // Fetch enough provinces
            })
            if (response && response.data) {
                const mappedProvinces = response.data.map(p => ({
                    value: String(p.id),
                    label: p.attributes.name
                }))
                setProvinceList(mappedProvinces)
            } else {
                setProvinceList([])
            }
        } catch (error) {
            console.error("Failed to fetch provinces", error)
            notifications.show({
                title: 'Error',
                message: 'Failed to load province list',
                color: 'red',
            })
        } finally {
            setLoadingProvinces(false)
        }
    }

    const handleChange = (name, val) => {
        setForm(prev => {
            const newForm = { ...prev, [name]: val }
            // If country changes, clear province and fetch new provinces
            if (name === 'country_id') {
                newForm.province_id = null
                fetchProvinces(val)
            }
            return newForm
        })

        // Clear error when user fills
        if (val) {
            setErrors(prev => ({ ...prev, [name]: '' }))
        }
    }

    const validate = () => {
        let isValid = true
        const newErrors = { name: '', country_id: '', province_id: '' }

        if (!form.name || form.name.trim() === '') {
            newErrors.name = 'City Name is required'
            isValid = false
        }

        if (!form.country_id) {
            newErrors.country_id = 'Country is required'
            isValid = false
        }

        if (!form.province_id) {
            newErrors.province_id = 'Province is required'
            isValid = false
        }

        setErrors(newErrors)
        return isValid
    }

    const handleSubmit = async () => {
        if (!validate()) return

        setLoading(true)
        try {
            const payload = {
                country_id: parseInt(form.country_id),
                province_id: parseInt(form.province_id),
                name: form.name,
                kiriminaja_id: 0 // As requested
            }

            let response
            if (dataDetail) {
                // Update
                response = await updateCity(dataDetail.id, payload)
            } else {
                // Create
                response = await createCity(payload)
            }

            if (response.success) {
                notifications.show({
                    title: 'Success',
                    message: dataDetail ? 'City updated successfully' : 'City created successfully',
                    color: 'teal',
                    icon: <IconCheck />,
                })
                reloadList()
                onCloseModal()
            } else {
                notifications.show({
                    title: 'Failed',
                    message: response.message || 'Operation failed',
                    color: 'red',
                    icon: <IconX />,
                })
            }
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: error.message || 'Internal Server Error',
                color: 'red',
                icon: <IconX />,
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box pos="relative">
            <LoadingOverlay visible={loading} overlayProps={{ radius: "sm", blur: 2 }} />

            <Box mb="md">
                <Select
                    label="Country"
                    placeholder={loadingCountries ? "Loading countries..." : "Select Country"}
                    data={countryList}
                    value={form.country_id}
                    onChange={(val) => handleChange('country_id', val)}
                    searchable
                    nothingFoundMessage="No country found"
                    renderOption={({ option, checked }) => {
                        const country = countryList.find(c => c.value === option.value)
                        return (
                            <Group flex="1" gap="xs">
                                {country?.iso_code_2 && (
                                    <Avatar src={`https://flagcdn.com/w40/${country.iso_code_2.toLowerCase()}.png`} alt={country.iso_code_2} size="sm" radius="xl" />
                                )}
                                <Text size="sm">{option.label}</Text>
                                {checked && <IconCheck style={{ marginInlineStart: 'auto' }} size={16} />}
                            </Group>
                        )
                    }}
                    leftSection={
                        (() => {
                            const selected = countryList.find(c => c.value === form.country_id)
                            return selected?.iso_code_2 ? (
                                <Avatar
                                    src={`https://flagcdn.com/w40/${selected.iso_code_2.toLowerCase()}.png`}
                                    size="xs"
                                    radius="xl"
                                />
                            ) : null
                        })()
                    }
                    error={errors.country_id}
                    withAsterisk
                    disabled={loadingCountries}
                    rightSection={loadingCountries ? <Loader size={16} /> : null}
                    mb="md"
                />

                <Select
                    label="Province"
                    placeholder={!form.country_id ? "Select Country First" : loadingProvinces ? "Loading provinces..." : "Select Province"}
                    data={provinceList}
                    value={form.province_id}
                    onChange={(val) => handleChange('province_id', val)}
                    searchable
                    nothingFoundMessage={!form.country_id ? "Select Country First" : "No province found"}
                    error={errors.province_id}
                    withAsterisk
                    disabled={!form.country_id || loadingProvinces}
                    rightSection={loadingProvinces ? <Loader size={16} /> : null}
                    mb="md"
                />

                <TextInput
                    label="City Name"
                    placeholder="Enter city name"
                    value={form.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    error={errors.name}
                    withAsterisk
                />
            </Box>

            <Group justify="flex-end" mt="xl">
                <Button variant="default" onClick={onCloseModal}>Cancel</Button>
                <Button onClick={handleSubmit}>
                    {dataDetail ? "Update" : "Submit"}
                </Button>
            </Group>
        </Box>
    )
}

export default FormCity
